import { createApi } from '@reduxjs/toolkit/query/react';

import { createApiBaseQuery, Methods } from '../shared';

import { ANALYTICS_CHUNK } from './const';
import {
  AnalyticsEndpoints as Endpoints,
  type ExportToExcelFinancialDetailsOfExpensesReportRequest,
  type ExportToExcelFinancialDetailsOfExpensesReportResponse,
  type ExportToExcelFinancialExpensesByArticlesReportRequest,
  type ExportToExcelFinancialExpensesByArticlesReportResponse,
  type ExportToExcelFinancialFactImplReportRequest,
  type ExportToExcelFinancialFactImplReportResponse,
  type ExportToExcelFinancialIncomesReportRequest,
  type ExportToExcelFinancialIncomesReportResponse,
  type ExportToExcelFinancialPnLReportRequest,
  type ExportToExcelFinancialPnLReportResponse,
  type ExportToExcelFinancialReportsRequest,
  type ExportToExcelFinancialReportsResponse,
  type GetAnalyticsFilterDataRequest,
  type GetAnalyticsFilterDataResponse,
  type GetAnalyticsServiceCategoriesRequest,
  type GetAnalyticsServiceCategoriesResponse,
  type GetAnalyticsWidgetRequest,
  type GetAnalyticsWidgetResponse,
  type GetBranchIndicatorsRequest,
  type GetBranchIndicatorsResponse,
  type GetDetailsOfExpensesReportRequest,
  type GetDetailsOfExpensesReportResponse,
  type GetExpenseByArticlesReportRequest,
  type GetExpenseByArticlesReportResponse,
  type GetFactImplReportRequest,
  type GetFactImplReportResponse,
  type GetFinancialStatisticsBySearchIdRequest,
  type GetFinancialStatisticsBySearchIdResponse,
  type GetFinancialStatisticsRequest,
  type GetFinancialStatisticsResponse,
  type GetIncomesReportRequest,
  type GetIncomesReportResponse,
  type GetOrganizationClientsGrowthRequest,
  type GetOrganizationClientsGrowthResponse,
  type GetPnLReportRequest,
  type GetPnLReportResponse,
} from './types';

export const analyticsApi2 = createApi({
  reducerPath: 'analyticsApi2',
  baseQuery: createApiBaseQuery(ANALYTICS_CHUNK),
  endpoints: ({ query, mutation }) => ({
    [Endpoints.GetAnalyticsFilterData]: query<
      GetAnalyticsFilterDataResponse,
      GetAnalyticsFilterDataRequest
    >({
      query: (params) => ({
        url: Endpoints.GetAnalyticsFilterData,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetAnalyticsWidget]: mutation<
      GetAnalyticsWidgetResponse,
      GetAnalyticsWidgetRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.GetAnalyticsWidget,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetAnalyticsServiceCategories]: mutation<
      GetAnalyticsServiceCategoriesResponse,
      GetAnalyticsServiceCategoriesRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.GetAnalyticsServiceCategories,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetFinancialStatistics]: query<
      GetFinancialStatisticsResponse,
      GetFinancialStatisticsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetFinancialStatistics,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetFinancialStatisticsBySearchId]: query<
      GetFinancialStatisticsBySearchIdResponse,
      GetFinancialStatisticsBySearchIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetFinancialStatisticsBySearchId,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetOrganizationClientsGrowth]: mutation<
      GetOrganizationClientsGrowthResponse,
      GetOrganizationClientsGrowthRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.GetOrganizationClientsGrowth,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetBranchIndicators]: mutation<
      GetBranchIndicatorsResponse,
      GetBranchIndicatorsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.GetBranchIndicators,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetPnLReport]: query<GetPnLReportResponse, GetPnLReportRequest>({
      query: (params) => ({
        url: Endpoints.GetPnLReport,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetIncomesReport]: query<
      GetIncomesReportResponse,
      GetIncomesReportRequest
    >({
      query: (params) => ({
        url: Endpoints.GetIncomesReport,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetFactImplReport]: query<
      GetFactImplReportResponse,
      GetFactImplReportRequest
    >({
      query: (params) => ({
        url: Endpoints.GetFactImplReport,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetExpenseByArticlesReport]: query<
      GetExpenseByArticlesReportResponse,
      GetExpenseByArticlesReportRequest
    >({
      query: (params) => ({
        url: Endpoints.GetExpenseByArticlesReport,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetDetailsOfExpensesReport]: query<
      GetDetailsOfExpensesReportResponse,
      GetDetailsOfExpensesReportRequest
    >({
      query: (params) => ({
        url: Endpoints.GetDetailsOfExpensesReport,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportToExcelFinancialPnLReport]: query<
      ExportToExcelFinancialPnLReportResponse,
      ExportToExcelFinancialPnLReportRequest
    >({
      query: (params) => ({
        url: Endpoints.ExportToExcelFinancialPnLReport,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportToExcelFinancialIncomesReport]: query<
      ExportToExcelFinancialIncomesReportResponse,
      ExportToExcelFinancialIncomesReportRequest
    >({
      query: (params) => ({
        url: Endpoints.ExportToExcelFinancialIncomesReport,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportToExcelFinancialFactImplReport]: query<
      ExportToExcelFinancialFactImplReportResponse,
      ExportToExcelFinancialFactImplReportRequest
    >({
      query: (params) => ({
        url: Endpoints.ExportToExcelFinancialFactImplReport,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportToExcelFinancialExpensesByArticlesReport]: query<
      ExportToExcelFinancialExpensesByArticlesReportResponse,
      ExportToExcelFinancialExpensesByArticlesReportRequest
    >({
      query: (params) => ({
        url: Endpoints.ExportToExcelFinancialExpensesByArticlesReport,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportToExcelFinancialDetailsOfExpensesReport]: query<
      ExportToExcelFinancialDetailsOfExpensesReportResponse,
      ExportToExcelFinancialDetailsOfExpensesReportRequest
    >({
      query: (params) => ({
        url: Endpoints.ExportToExcelFinancialDetailsOfExpensesReport,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ExportToExcelFinancialReports]: query<
      ExportToExcelFinancialReportsResponse,
      ExportToExcelFinancialReportsRequest
    >({
      query: (params) => ({
        url: Endpoints.ExportToExcelFinancialReports,
        method: Methods.GET,
        params,
      }),
    }),
  }),
});

export const {
  useGetAnalyticsFilterDataQuery,
  useLazyGetAnalyticsFilterDataQuery,
  useGetAnalyticsWidgetMutation,
  useGetAnalyticsServiceCategoriesMutation,
  useGetFinancialStatisticsQuery,
  useLazyGetFinancialStatisticsQuery,
  useGetFinancialStatisticsBySearchIdQuery,
  useLazyGetFinancialStatisticsBySearchIdQuery,
  useGetOrganizationClientsGrowthMutation,
  useGetBranchIndicatorsMutation,
  useGetPnLReportQuery,
  useLazyGetPnLReportQuery,
  useGetIncomesReportQuery,
  useLazyGetIncomesReportQuery,
  useGetFactImplReportQuery,
  useLazyGetFactImplReportQuery,
  useGetExpenseByArticlesReportQuery,
  useLazyGetExpenseByArticlesReportQuery,
  useGetDetailsOfExpensesReportQuery,
  useLazyGetDetailsOfExpensesReportQuery,
  useExportToExcelFinancialPnLReportQuery,
  useLazyExportToExcelFinancialPnLReportQuery,
  useExportToExcelFinancialIncomesReportQuery,
  useLazyExportToExcelFinancialIncomesReportQuery,
  useExportToExcelFinancialFactImplReportQuery,
  useLazyExportToExcelFinancialFactImplReportQuery,
  useExportToExcelFinancialExpensesByArticlesReportQuery,
  useLazyExportToExcelFinancialExpensesByArticlesReportQuery,
  useExportToExcelFinancialDetailsOfExpensesReportQuery,
  useLazyExportToExcelFinancialDetailsOfExpensesReportQuery,
  useExportToExcelFinancialReportsQuery,
  useLazyExportToExcelFinancialReportsQuery,
} = analyticsApi2;
